import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Pagination,
  Chip,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import api from '../api/client';
import { getApiOrigin } from '../api/config';
import QueryError from '../components/QueryError';
import { segmentedControlSx, tableTagSx } from '../styles/uiTokens';

const API_ORIGIN = getApiOrigin();

const WASHER_OPTIONS = ['전체', '세차기1', '세차기2', '세차기3'];

function toDateStr(date) {
  return date.toISOString().slice(0, 10);
}

const UsagePage = () => {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [tab, setTab] = useState(1); // 0=레거시, 1=키오스크

  // ── 레거시 탭 state ──
  const [startDate, setStartDate] = useState(toDateStr(firstOfMonth));
  const [endDate, setEndDate]     = useState(toDateStr(today));
  const [type, setType]           = useState('');
  const [page, setPage]           = useState(1);
  const [data, setData]           = useState({ total: 0, list: [] });
  const [loading, setLoading]     = useState(false);
  // 조회 실패를 "0건"과 구분하기 위한 상태 (이전에는 console.error 만 했다)
  const [loadError, setLoadError] = useState(null);

  // ── 키오스크 탭 state ──
  const [kStartDate, setKStartDate] = useState(toDateStr(firstOfMonth));
  const [kEndDate, setKEndDate]     = useState(toDateStr(today));
  const [kWasherId, setKWasherId]   = useState('전체');
  const [kPage, setKPage]           = useState(1);
  const [kData, setKData]           = useState({ total: 0, list: [] });
  const [kLoading, setKLoading]     = useState(false);
  const [kLoadError, setKLoadError] = useState(null);
  const [photoDialog, setPhotoDialog] = useState({ open: false, url: null, plate: '' });

  const handleSearch = () => { setPage(1); fetchData(); };
  const handleKSearch = () => { setKPage(1); fetchKioskData(); };

  const handleDownloadLegacy = async () => {
    try {
      const res = await api.get('/usage', {
        params: { startDate, endDate, type: type || undefined, page: 1, limit: 5000 },
      });
      const rows = (res.data.list || []).map((r) => ({
        '날짜/시간': new Date(r.date).toLocaleString('ko-KR'),
        '번호판': r.lp,
        '이름': r.name,
        '연락처': r.phone,
        '구분': r.lp_type,
        '상품명': r.productName,
        '위치': r.location,
        '이번달 세차수': r.washCount,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '이용내역');
      XLSX.writeFile(wb, `이용내역_${startDate}_${endDate}.xlsx`);
    } catch (err) {
      console.error('엑셀 다운로드 실패:', err);
    }
  };

  const handleDownloadKiosk = async () => {
    try {
      const res = await api.get('/usage/kiosk', {
        params: {
          startDate: kStartDate,
          endDate: kEndDate,
          washerId: kWasherId === '전체' ? undefined : kWasherId,
          page: 1,
          limit: 5000,
        },
      });
      const rows = (res.data.list || []).map((r) => ({
        '날짜/시간': new Date(r.usedAt).toLocaleString('ko-KR'),
        '번호판': r.licensePlate,
        '세차기': r.washerId,
        '구분': r.source === 'subscription' ? '구독' : r.source === 'rf' ? 'RF' : '카드',
        '회원명': r.memberName,
        '연락처': r.memberPhone,
        '상품명': r.productName,
        '금액': r.amount ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '키오스크이용');
      XLSX.writeFile(wb, `키오스크이용_${kStartDate}_${kEndDate}.xlsx`);
    } catch (err) {
      console.error('엑셀 다운로드 실패:', err);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/usage', {
        params: { startDate, endDate, type: type || undefined, page, limit: 50 },
      });
      setData(res.data);
      setLoadError(null);
    } catch (err) {
      // 실패를 "이용 0건"으로 보이게 하지 않는다 — 정산 대사에서 판단이 반대로 뒤집힌다
      setData({ total: 0, list: [] });
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, type, page]);

  const fetchKioskData = useCallback(async () => {
    setKLoading(true);
    try {
      const res = await api.get('/usage/kiosk', {
        params: {
          startDate: kStartDate,
          endDate: kEndDate,
          washerId: kWasherId === '전체' ? undefined : kWasherId,
          page: kPage,
          limit: 50,
        },
      });
      setKData(res.data);
      setKLoadError(null);
    } catch (err) {
      setKData({ total: 0, list: [] });
      setKLoadError(err);
    } finally {
      setKLoading(false);
    }
  }, [kStartDate, kEndDate, kWasherId, kPage]);

  useEffect(() => { if (tab === 0) fetchData(); },      [fetchData, tab]);
  useEffect(() => { if (tab === 1) fetchKioskData(); }, [fetchKioskData, tab]);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        이용 관리
      </Typography>

      {/* 탭 */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="전체 이용 내역 (레거시)" />
        <Tab label="키오스크 이용" />
      </Tabs>

      {/* ───────────── 레거시 탭 ───────────── */}
      {tab === 0 && (
        <>
          {/* 필터 */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField
                label="시작일" type="date" size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: { xs: '48%', sm: 160 } }}
              />
              <TextField
                label="종료일" type="date" size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: { xs: '48%', sm: 160 } }}
              />
              <ToggleButtonGroup
                size="small"
                value={type}
                exclusive
                onChange={(_, v) => setType(v ?? '')}
                sx={segmentedControlSx}
              >
                <ToggleButton value="">전체</ToggleButton>
                <ToggleButton value="member">회원</ToggleButton>
                <ToggleButton value="guest">비회원</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'auto', px: 2.5 }}
              >
                조회
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadLegacy}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'auto', px: 2 }}
              >
                엑셀
              </Button>
            </Box>
          </Paper>

          {/* 요약 */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              총 <strong>{data.total?.toLocaleString()}건</strong> 조회됨
            </Typography>
          </Paper>

          <QueryError error={loadError} onRetry={fetchData} context="이용 내역" />

          {/* 테이블 — 모바일은 핵심 컬럼만 */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>날짜/시간</TableCell>
                  <TableCell>번호판</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>이름</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>연락처</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>구분</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>상품명</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>위치</TableCell>
                  <TableCell align="center">세차수</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">조회 중...</TableCell>
                  </TableRow>
                ) : loadError ? null : data.list.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                      해당 기간 이용 내역이 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  data.list.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(row.date).toLocaleString('ko-KR', {
                          month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.lp}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.name}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', display: { xs: 'none', lg: 'table-cell' } }}>{row.phone}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={row.lp_type}
                          size="small"
                          color={row.lp_type === '회원' ? 'primary' : 'default'}
                          variant="outlined"
                          sx={tableTagSx(52)}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.productName}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', display: { xs: 'none', lg: 'table-cell' } }}>{row.location}</TableCell>
                      <TableCell align="center">
                        {row.washCount !== '-' ? `${row.washCount}회` : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data.total > 50 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(data.total / 50)}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* ───────────── 키오스크 탭 ───────────── */}
      {tab === 1 && (
        <>
          {/* 필터 */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              <TextField
                label="시작일" type="date" size="small"
                value={kStartDate}
                onChange={(e) => setKStartDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: { xs: '48%', sm: 160 } }}
              />
              <TextField
                label="종료일" type="date" size="small"
                value={kEndDate}
                onChange={(e) => setKEndDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: { xs: '48%', sm: 160 } }}
              />
              <FormControl size="small" sx={{ width: { xs: '48%', sm: 120 } }}>
                <InputLabel>세차기</InputLabel>
                <Select
                  label="세차기"
                  value={kWasherId}
                  onChange={(e) => setKWasherId(e.target.value)}
                >
                  {WASHER_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleKSearch}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'auto', px: 2.5 }}
              >
                조회
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadKiosk}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'auto', px: 2 }}
              >
                엑셀
              </Button>
            </Box>
          </Paper>

          {/* 요약 */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              총 <strong>{kData.total?.toLocaleString()}건</strong> 조회됨
            </Typography>
          </Paper>

          <QueryError error={kLoadError} onRetry={fetchKioskData} context="키오스크 이용 내역" />

          {/* 테이블 */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>날짜/시간</TableCell>
                  <TableCell>번호판</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>세차기</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>구분</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>회원명</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>연락처</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>상품명</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>사진</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">조회 중...</TableCell>
                  </TableRow>
                ) : kLoadError ? null : kData.list.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                      해당 기간 이용 내역이 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  kData.list.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(row.usedAt).toLocaleString('ko-KR', {
                          month: '2-digit', day: '2-digit',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.licensePlate}</TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={row.washerId !== '-' ? row.washerId : '미지정'}
                          size="small"
                          color={row.washerId !== '-' ? 'secondary' : 'default'}
                          variant="outlined"
                          sx={tableTagSx(64)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={row.source === 'subscription' ? '구독' : row.source === 'rf' ? 'RF' : '카드'}
                          size="small"
                          color={row.source === 'subscription' ? 'success' : row.source === 'rf' ? 'warning' : 'primary'}
                          variant="outlined"
                          sx={tableTagSx(52)}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.memberName}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary', display: { xs: 'none', lg: 'table-cell' } }}>{row.memberPhone}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{row.productName}</TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        {row.photoFile ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setPhotoDialog({
                              open: true,
                              url: `${API_ORIGIN}/static/lpr-captures/${row.photoFile}`,
                              plate: row.licensePlate,
                            })}
                          >
                            보기
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled">-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {kData.total > 50 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(kData.total / 50)}
                page={kPage}
                onChange={(_, v) => setKPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* 사진 다이얼로그 */}
      <Dialog
        open={photoDialog.open}
        onClose={() => setPhotoDialog({ open: false, url: null, plate: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {photoDialog.plate} — 인식 캡처
          <IconButton onClick={() => setPhotoDialog({ open: false, url: null, plate: '' })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, textAlign: 'center' }}>
          {photoDialog.url && (
            <img
              src={photoDialog.url}
              alt={photoDialog.plate}
              style={{ maxWidth: '100%', borderRadius: 8 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UsagePage;
